import React from 'react';
import { useSelector } from 'react-redux';
import { GridPageLayout } from '@lux/grid';
import { StandardCarousel, Article } from '@browsing/standard-carousel';
import { getSemanticArticlesCarouselData } from '@client/redux/modules/semanticArticles/selectors';
import { ImageSize, useConfig } from '@browsing/config';
import { trackSelectContent, trackSelectItem, trackViewItemList } from '@browsing/tracking';
import { useTranslate } from '@pegasus/translation';
import { NavigationSourceType, CatalogType } from '@browsing/utils';
import { ElementType } from '@pegasus/tracker';
import { ErrorBoundary } from '@browsing/components';
import { CarouselContainer } from './index.styles';
// @TODO - Keep it for future testing
// import { mockSemanticArticlesCarouselData } from './__mock__/sac';

const SemanticArticleCarouselInternal = () => {
    const config = useConfig();
    const {
        title: carouselTitle,
        articles: semanticArticles,
        totalCount,
        themeHash,
        loading,
        error,
    } = useSelector(getSemanticArticlesCarouselData);
    const translation = useTranslate();

    if (!semanticArticles?.length) {
        return null;
    }

    const getLastSemanticArticleImage = () => {
        const lastArticle = semanticArticles[semanticArticles.length - 1];
        return config.getImage(lastArticle?.media![0].path, ImageSize.Medium);
    };

    const slicedSemanticArticles = semanticArticles.slice(0, 16);

    const sacCarouselValue = 'sac_carousel';

    const trackArticleSelection = (product: Article, idx: number) => {
        trackSelectItem(
            {
                element: sacCarouselValue,
                context: sacCarouselValue,
                item: product,
                itemListName: sacCarouselValue,
                themeHash,
            },
            {
                customMapper: () => ({ index: idx }),
            },
        );
    };

    const trackArticlesView = (items: Article[]) => {
        trackViewItemList(
            {
                items,
                context: sacCarouselValue,
                itemListName: sacCarouselValue,
                element: sacCarouselValue,
                themeHash,
            },
            {
                customMapper: () => ({
                    item_list_name: sacCarouselValue,
                }),
            },
        );
    };

    const trackArticlesScroll = () => {
        trackSelectContent(sacCarouselValue as unknown as ElementType, 'swipe', {
            themeHash,
        });
    };

    const trackSeeAllLinkClick = () => {
        trackSelectContent(sacCarouselValue as unknown as ElementType, 'click', {
            contentId: 'see_all',
            themeHash,
        });
    };

    return (
        <GridPageLayout>
            <CarouselContainer>
                <StandardCarousel
                    title={carouselTitle}
                    carouselItems={slicedSemanticArticles as unknown as Article[]}
                    loading={loading}
                    error={!!error}
                    carouselId="semantic-articles-mylounge"
                    catalogType={CatalogType.SacCarousel}
                    themeHash={themeHash}
                    tracking={{
                        source: NavigationSourceType.SacCarousel,
                        trackArticleSelection,
                        trackArticlesView,
                        trackArticlesScroll,
                    }}
                    translationPath="article"
                    seeAllConfig={{
                        image: getLastSemanticArticleImage(),
                        href: `/campaigns/semantic-articles?themeHash=${themeHash}`,
                        buttontext: translation('TP4U.cta', { count: totalCount }), // @TODO - change to component specific translation key
                        onClick: trackSeeAllLinkClick,
                        titlePosition: 'bottom',
                    }}
                />
            </CarouselContainer>
        </GridPageLayout>
    );
};

export const SemanticArticleCarousel = () => (
    <ErrorBoundary componentName="SemanticArticleCarousel">
        <SemanticArticleCarouselInternal />
    </ErrorBoundary>
);
